import {
  useId,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "../sheet";
import { useTriggerWidth } from "../use-trigger-width";
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen";
import { cn } from "../../../lib/utils";

/** `auto` picks the sheet only on a small screen and only when the trigger is
 * not already inside a dialog. The other two force one shape. */
export type SelectorPresentation = "auto" | "popover" | "sheet";

export interface SelectorTriggerRenderProps {
  ref: Ref<HTMLButtonElement>;
  open: boolean;
  "aria-haspopup": "listbox" | "dialog";
  "aria-expanded": boolean;
  "aria-controls"?: string;
  "data-state": "open" | "closed";
  onClick?: () => void;
}

export interface SelectorMenuSurfaceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presentation?: SelectorPresentation;
  renderTrigger: (props: SelectorTriggerRenderProps) => ReactNode;
  children: ReactNode;
  /** `content` sizes the panel to its own rows; `trigger` slaves it to the
   * trigger's measured width. */
  widthMode?: "content" | "trigger";
  minWidth?: number;
  maxWidth?: number;
  /** Trigger-width mode only: an independent floor for the panel. */
  menuMinWidth?: number;
  className?: string;
  /** Named for screen readers; Radix otherwise announces "Sheet dialog". */
  title?: string;
  /** Popover only: the id of a heading inside `children` that names the
   * dialog. The sheet is named by `title`. */
  contentAriaLabelledBy?: string;
  "data-testid"?: string;
}

export function SelectorMenuSurface({
  open,
  onOpenChange,
  presentation = "auto",
  renderTrigger,
  children,
  widthMode = "content",
  minWidth,
  maxWidth,
  menuMinWidth,
  className,
  title = "Select accounts",
  contentAriaLabelledBy,
  "data-testid": testId,
}: SelectorMenuSurfaceProps) {
  // Every hook runs on every render: the resolved presentation must never
  // change the hook order, or a viewport resize unmounts the whole tree.
  const isSmallScreen = useIsSmallScreen();
  const sheetId = useId();
  const { triggerRef, triggerWidthStyle } = useTriggerWidth<HTMLButtonElement>(
    open,
    menuMinWidth
  );

  // Radix gives dialog, sheet and popover content `role="dialog"` and alert
  // dialogs `role="alertdialog"`, so these two cover every surface a second
  // sheet must not open inside. `ModalStackContext` cannot answer this: the
  // Insights app and every popover wrap their children in a provider, so a
  // non-null context means nothing.
  const [insideDialog, setInsideDialog] = useState(false);
  useLayoutEffect(() => {
    setInsideDialog(
      Boolean(
        triggerRef.current?.closest('[role="dialog"],[role="alertdialog"]')
      )
    );
  }, [triggerRef]);

  const resolved: "popover" | "sheet" =
    presentation === "auto"
      ? isSmallScreen && !insideDialog
        ? "sheet"
        : "popover"
      : presentation;

  if (resolved === "sheet") {
    return (
      <>
        {renderTrigger({
          ref: triggerRef,
          open,
          // The sheet is a Radix dialog, whatever it holds. Advertising a
          // listbox here would promise a shape the opened surface does not have.
          "aria-haspopup": "dialog",
          "aria-expanded": open,
          "aria-controls": open ? sheetId : undefined,
          "data-state": open ? "open" : "closed",
          onClick: () => onOpenChange(!open),
        })}
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            id={sheetId}
            side="bottom"
            // The trigger is outside the Sheet and is not a `SheetTrigger`, so
            // Radix has no ref to return focus to on close.
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
            // The sheet's own close button is absolutely placed over the
            // content, where a sticky group header could cover it. The header
            // row below carries it instead.
            showCloseButton={false}
            // A Sheet's base layer sits below the popover layer; the offset
            // lifts it clear of anything the trigger itself is sitting in.
            zIndexOffset={60}
            data-testid={testId}
            className={cn(
              "flex max-h-[85vh] flex-col gap-0 rounded-t-2xl p-0 pb-[env(safe-area-inset-bottom)]",
              "[&_[data-slot=command]]:max-h-none [&_[data-slot=command]]:border-0 [&_[data-slot=command]]:shadow-none",
              className
            )}
          >
            {/* Always a close button: overlay tap and Esc are not affordances
                on a phone, and a multi-select sheet does not dismiss on
                choosing. It sits above the scrolling list, never inside it. */}
            <div
              data-selector-sheet-header=""
              className="flex shrink-0 items-center gap-3 border-b border-neutral-100 py-2 pr-2 pl-4"
            >
              <SheetTitle className="min-w-0 flex-1 truncate font-inter text-base leading-6 font-semibold text-neutral-900">
                {title}
              </SheetTitle>
              <SheetClose className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                <i
                  aria-hidden
                  className="fa-solid fa-xmark text-[16px] leading-none"
                />
                <span className="sr-only">Close</span>
              </SheetClose>
            </div>
            <div
              data-selector-sheet-body=""
              className="flex min-h-0 flex-1 flex-col"
            >
              {children}
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {renderTrigger({
          ref: triggerRef,
          open,
          "aria-haspopup": "listbox",
          "aria-expanded": open,
          "data-state": open ? "open" : "closed",
        })}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        collisionPadding={8}
        motion="rise"
        widthMode={widthMode === "content" ? "content" : "trigger"}
        style={
          widthMode === "content"
            ? ({
                // The clamp lives in the class below, not inline: jsdom throws
                // on `min()` inside `getComputedStyle` and breaks every role
                // query on the subtree. Both fallbacks must be lengths, never
                // `none`: one invalid argument drops the whole declaration.
                "--selector-menu-min": minWidth ? `${minWidth}px` : "0px",
                "--selector-menu-max": maxWidth ? `${maxWidth}px` : "100vw",
              } as CSSProperties)
            : triggerWidthStyle
        }
        aria-labelledby={contentAriaLabelledBy}
        data-testid={testId}
        className={cn(
          "rounded-lg border-0 p-0 shadow-none",
          // The trigger's width is a floor INSIDE the clamp: min-width beats
          // max-width in CSS, so an unbounded trigger term would defeat the
          // panel's own maximum.
          widthMode === "content" &&
            "min-w-[min(max(var(--radix-popover-trigger-width),var(--selector-menu-min)),var(--selector-menu-max),calc(100vw-2rem))] max-w-[min(var(--selector-menu-max),calc(100vw-2rem))]",
          // Below `sm` it takes the trigger's width: a 360px floor against a
          // narrower trigger cannot both align to its left edge and stay inside
          // the safe area, so Radix shifts it off the control.
          widthMode === "content" &&
            "max-sm:w-[var(--radix-popover-trigger-width)] max-sm:min-w-[var(--radix-popover-trigger-width)] max-sm:max-w-[var(--radix-popover-trigger-width)]",
          className
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
